/**
 * Shared helpers for the recording players across the app.
 *
 * Blink browsers (Chrome/Edge) report `duration === Infinity` for blobs produced
 * by MediaRecorder (our webm/mp4 recordings) until a seek to the end forces the
 * browser to compute the real length. Left unhandled this shows up as a native
 * scrubber that jumps from full to empty, or a custom player rendering NaN
 * durations and a dead progress bar. Wire `ensureFiniteDuration` to the media
 * element's `loadedmetadata` to resolve it everywhere.
 */

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function ensureFiniteDuration(
  el: HTMLMediaElement,
  onResolved?: (duration: number) => void,
): void {
  if (Number.isFinite(el.duration)) {
    onResolved?.(el.duration);
    return;
  }

  const handleSeek = () => {
    el.removeEventListener("timeupdate", handleSeek);
    el.currentTime = 0;
    onResolved?.(el.duration);
  };

  el.addEventListener("timeupdate", handleSeek);
  // a large but finite seek forces Blink to read the true duration
  el.currentTime = Number.MAX_SAFE_INTEGER;
}
